import React, { useCallback, useEffect, useState } from "react"
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    Switch,
    TableCell,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import { Delete } from "@mui/icons-material"
import { handlePromise } from "../../../../utils/handlePromisse"
import {
    addFeatureFlagUserOverride,
    FeatureFlagDetail,
    FeatureFlagOverride,
    getFeatureFlagDetail,
    removeFeatureFlagUserOverride,
} from "../../../../services/feature-flag.service"
import { getPaginatedUsers } from "../../../../services/userService"

type UserOption = {
    id: number
    name: string
    email: string
}

type Props = {
    open: boolean
    flagName: string | null
    onClose: () => void
    onError: (message: string) => void
}

export const OverridesDialog: React.FC<Props> = ({ open, flagName, onClose, onError }) => {
    const [detail, setDetail] = useState<FeatureFlagDetail | null>(null)
    const [loading, setLoading] = useState(false)

    const [searchInput, setSearchInput] = useState("")
    const [userOptions, setUserOptions] = useState<UserOption[]>([])
    const [searchingUsers, setSearchingUsers] = useState(false)
    const [selectedUser, setSelectedUser] = useState<UserOption | null>(null)
    const [newOverrideEnabled, setNewOverrideEnabled] = useState(true)

    const refresh = useCallback(async () => {
        if (!flagName) return
        setLoading(true)
        const [result, error] = await handlePromise(getFeatureFlagDetail(flagName))
        setLoading(false)
        if (error) {
            onError(error.response?.data?.message || "Erro ao carregar detalhes da flag")
            return
        }
        setDetail(result.data)
    }, [flagName, onError])

    useEffect(() => {
        if (open && flagName) {
            refresh()
            setSelectedUser(null)
            setSearchInput("")
            setUserOptions([])
            setNewOverrideEnabled(true)
        }
    }, [open, flagName, refresh])

    useEffect(() => {
        if (!open) return
        const trimmed = searchInput.trim()
        if (trimmed.length < 2) {
            setUserOptions([])
            return
        }
        let cancelled = false
        const timer = setTimeout(async () => {
            setSearchingUsers(true)
            const [result, error] = await handlePromise(
                getPaginatedUsers({ page: 1, size: 10, query: trimmed })
            )
            if (cancelled) return
            setSearchingUsers(false)
            if (error) return
            const items = (result.data?.data ?? []).map((u: any) => ({
                id: u.id ?? u.id_User,
                name: u.name ?? u.name_User ?? "—",
                email: u.email ?? u.email_User ?? "",
            }))
            setUserOptions(items)
        }, 300)
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [searchInput, open])

    const handleAdd = async () => {
        if (!flagName || !selectedUser) return
        const [, error] = await handlePromise(
            addFeatureFlagUserOverride(flagName, selectedUser.id, newOverrideEnabled)
        )
        if (error) {
            onError(error.response?.data?.message || "Erro ao adicionar override")
            return
        }
        setSelectedUser(null)
        setSearchInput("")
        await refresh()
    }

    const handleRemove = async (override: FeatureFlagOverride) => {
        if (!flagName) return
        const [, error] = await handlePromise(
            removeFeatureFlagUserOverride(flagName, override.userId)
        )
        if (error) {
            onError(error.response?.data?.message || "Erro ao remover override")
            return
        }
        await refresh()
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                Overrides — <Box component="span" sx={{ fontFamily: "monospace" }}>{flagName}</Box>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Overrides têm prioridade sobre o estado global e o rollout. Use para liberar
                    a feature pra testers específicos (allowlist) ou bloquear para um usuário
                    específico mesmo quando a flag está ligada (blocklist).
                </Typography>

                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mb: 2 }}>
                    <Autocomplete
                        sx={{ flex: 1 }}
                        options={userOptions}
                        value={selectedUser}
                        onChange={(_, value) => setSelectedUser(value)}
                        onInputChange={(_, value) => setSearchInput(value)}
                        getOptionLabel={(option) => `${option.name} (${option.email})`}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        loading={searchingUsers}
                        noOptionsText={
                            searchInput.length < 2
                                ? "Digite ao menos 2 caracteres"
                                : searchingUsers
                                ? "Buscando..."
                                : "Nenhum usuário encontrado"
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Buscar usuário (nome ou email)"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {searchingUsers ? <CircularProgress size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                            <Switch
                                checked={newOverrideEnabled}
                                onChange={(_, v) => setNewOverrideEnabled(v)}
                            />
                        }
                        label={newOverrideEnabled ? "Liberar" : "Bloquear"}
                    />
                    <Button
                        variant="contained"
                        onClick={handleAdd}
                        disabled={!selectedUser}
                        sx={{ height: 56 }}
                    >
                        Adicionar
                    </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Usuário</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell align="center">Estado</TableCell>
                                    <TableCell align="right">Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {detail?.overrides.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                                Nenhum override cadastrado
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {detail?.overrides.map((override) => (
                                    <TableRow hover key={override.userId}>
                                        <TableCell>{override.userName}</TableCell>
                                        <TableCell>{override.userEmail}</TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                size="small"
                                                label={override.enabled ? "Liberado" : "Bloqueado"}
                                                color={override.enabled ? "success" : "error"}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Remover override" placement="top">
                                                <IconButton onClick={() => handleRemove(override)}>
                                                    <Delete />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    )
}